import { useState } from 'react';
import SafeIcon from './SafeIcon';
import './setup.css';
import { useApp } from '../i18n/AppContext';
import { useUi } from '../store/ui';
import { useDemo } from '../store/DemoStore';
import { DEMO_TODAY, workspaces, accountProblem, onAccount } from '../store/seed';

const REQUEST_PREVIEW_LENGTH = 70;

export default function AgencyHome({
  onOpenWorkspace,
  onViewClient,
}: {
  onOpenWorkspace: (id: string) => void;
  onViewClient: (id: string) => void;
  onOpenReview?: (id: string) => void;
}) {
  const { translate, formatCount, language } = useApp();
  const { navigate, notify, actor } = useUi();
  const [replying, setReplying] = useState<string | null>(null);
  const [reply, setReply] = useState('');
  const { state, commands } = useDemo();
  const today = new Intl.DateTimeFormat(language, {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(new Date(2026, 10, DEMO_TODAY));
  const dayLabel = (day: number) => {
    if (day === DEMO_TODAY) return translate('Today {time}', { time: '' }).trim();
    if (day === DEMO_TODAY + 1) return translate('Tomorrow {time}', { time: '' }).trim();
    return new Intl.DateTimeFormat(language, {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
    }).format(new Date(2026, 10, day));
  };

  type Row = {
    key: string;
    cause: string;
    workspaceId: string;
    workspaceName: string;
    affected: string;
    owner: string;
    action: string;
    run: () => void;
    done?: string;
  };
  const rows: Row[] = [];
  workspaces.forEach((workspace) => {
    const posts = state.posts.filter((post) => post.workspaceId === workspace.id);
    state.accounts
      .filter((candidate) => candidate.workspaceId === workspace.id && accountProblem(candidate))
      .forEach((account) => {
        const key = `remind-${account.id}`;
        rows.push({
          key,
          cause: translate(
            account.health === 'Needs reconnecting'
              ? '{network} connection expired'
              : '{network} is not connected',
            { network: account.network }
          ),
          workspaceId: workspace.id,
          workspaceName: workspace.name,
          affected: formatCount(
            posts.filter(
              (post) =>
                onAccount(post, account) && ['Scheduled', 'Needs your review'].includes(post.status)
            ).length,
            'post'
          ),
          owner: account.owner,
          action: translate('Send reminder'),
          done: state.reminders[key]
            ? translate('Reminder sent at {time}', { time: state.reminders[key] })
            : undefined,
          run: () => {
            commands.remind(key);
            notify(translate('Reminder sent to {name} (simulated)', { name: account.owner }));
          },
        });
      });
    const postsAwaitingReview = posts.filter((post) => post.status === 'Needs your review').length;
    if (postsAwaitingReview)
      rows.push({
        key: `review-${workspace.id}`,
        cause: translate('Awaiting client review'),
        workspaceId: workspace.id,
        workspaceName: workspace.name,
        affected: formatCount(postsAwaitingReview, 'post'),
        owner: workspace.owner,
        action: translate('Open workspace'),
        run: () => onOpenWorkspace(workspace.id),
      });
    posts
      .filter((post) => post.status === 'Needs your input')
      .forEach((post) =>
        rows.push({
          key: `input-${post.id}`,
          cause: translate('Material missing'),
          workspaceId: workspace.id,
          workspaceName: workspace.name,
          affected: post.title,
          owner: workspace.owner,
          action: translate('Open post'),
          run: () => navigate({ workspaceId: workspace.id, page: 'post', id: post.id }),
        })
      );
    posts
      .filter((post) => post.timeRequest)
      .forEach((post) =>
        rows.push({
          key: `time-${post.id}`,
          cause: translate('New time suggested by the client'),
          workspaceId: workspace.id,
          workspaceName: workspace.name,
          affected: `${post.title} · ${post.channel}`,
          owner: post.timeRequest!.by,
          action: translate('Open post'),
          run: () => navigate({ workspaceId: workspace.id, page: 'post', id: post.id }),
        })
      );
    posts
      .filter((post) => post.needsNewTime && !post.timeRequest)
      .forEach((post) =>
        rows.push({
          key: `newtime-${post.id}`,
          cause: translate('Approved post needs a new time'),
          workspaceId: workspace.id,
          workspaceName: workspace.name,
          affected: `${post.title} · ${post.channel}`,
          owner: workspace.owner,
          action: translate('Choose a new time'),
          run: () => navigate({ workspaceId: workspace.id, page: 'post', id: post.id }),
        })
      );
    state.tasks
      .filter((task) => task.workspaceId === workspace.id && task.status === 'With agency')
      .forEach((task) =>
        rows.push({
          key: `task-${task.id}`,
          cause: translate('Task handed to the agency'),
          workspaceId: workspace.id,
          workspaceName: workspace.name,
          affected: task.showAsWritten ? task.title : translate(task.title, task.variables),
          owner: task.by,
          action: translate('Open tasks'),
          run: () => navigate({ workspaceId: workspace.id, page: 'ideas', id: 'tasks' }),
        })
      );
    state.tasks
      .filter((task) => task.workspaceId === workspace.id && task.status === 'Waiting')
      .forEach((task) =>
        rows.push({
          key: `wait-${task.id}`,
          cause: translate('AMY is waiting for the client'),
          workspaceId: workspace.id,
          workspaceName: workspace.name,
          affected: task.showAsWritten ? task.title : translate(task.title, task.variables),
          owner: workspace.owner,
          action: translate('Open tasks'),
          run: () => navigate({ workspaceId: workspace.id, page: 'ideas', id: 'tasks' }),
        })
      );
    state.requests
      .filter((request) => request.workspaceId === workspace.id && request.status === 'Open')
      .forEach((request) =>
        rows.push({
          key: `rq-${request.id}`,
          cause: translate('Message from the client'),
          workspaceId: workspace.id,
          workspaceName: workspace.name,
          affected:
            request.text.length > REQUEST_PREVIEW_LENGTH
              ? `${request.text.slice(0, REQUEST_PREVIEW_LENGTH)}…`
              : request.text,
          owner: request.from,
          action: translate('Reply'),
          run: () => {
            setReplying(request.id);
            setReply('');
          },
        })
      );
    posts
      .filter((post) => post.results?.some((result) => !result.succeeded))
      .forEach((post) =>
        rows.push({
          key: `partial-${post.id}`,
          cause: translate('Published on {ok} of {n}', {
            ok: post.results!.filter((result) => result.succeeded).length,
            n: post.results!.length,
          }),
          workspaceId: workspace.id,
          workspaceName: workspace.name,
          affected: post.title,
          owner: workspace.contributor,
          action: translate('Open post'),
          run: () => navigate({ workspaceId: workspace.id, page: 'post', id: post.id }),
        })
      );
  });
  const upcoming = state.posts
    .filter((post) => post.status === 'Scheduled' && post.day >= DEMO_TODAY)
    .sort((first, second) => first.day - second.day || first.time.localeCompare(second.time))
    .slice(0, 5);
  const headers = [
    translate('Cause'),
    translate('Workspace'),
    translate('Affected items'),
    translate('Owner'),
    translate('Action'),
  ];

  return (
    <div className="content-stack">
      <div className="page-title-row">
        <div>
          <h1>{translate('Agency home')}</h1>
          <p className="subtitle">Hakusana · {today}</p>
        </div>
      </div>
      <div className="panel">
        <div className="panel-header">
          <h2>{translate('Needs attention')}</h2>
          <span className="status review">{formatCount(rows.length, 'item')}</span>
        </div>
        {rows.length === 0 ? (
          <p className="muted">{translate('Nothing needs attention right now.')}</p>
        ) : (
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  {headers.map((value) => (
                    <th key={value}>{value}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.key}>
                    <td>{row.cause}</td>
                    <td>{row.workspaceName}</td>
                    <td>{row.affected}</td>
                    <td>{row.owner}</td>
                    <td>
                      {row.done ? (
                        <span className="muted">{row.done}</span>
                      ) : (
                        <button className="button small" onClick={row.run}>
                          {row.action}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <div className="two-column">
        <div className="panel">
          <h2>{translate('Upcoming publications')}</h2>
          {upcoming.map((post) => (
            <button
              className="checklist-item list-button"
              key={post.id}
              onClick={() =>
                navigate({ workspaceId: post.workspaceId as never, page: 'post', id: post.id })
              }
            >
              <SafeIcon name="FiCalendar" />
              <span style={{ flex: 1 }}>
                <strong>{post.title}</strong>
                <span className="muted">
                  {' '}
                  · {workspaces.find((workspace) => workspace.id === post.workspaceId)?.name} ·{' '}
                  {post.channel}
                </span>
              </span>
              <span className="muted">
                {dayLabel(post.day)} {post.time}
              </span>
            </button>
          ))}
        </div>
        <div className="panel">
          <h2>{translate('Workspaces')}</h2>
          {workspaces.map((workspace) => {
            const posts = state.posts.filter((post) => post.workspaceId === workspace.id);
            const problems = state.accounts.filter(
              (account) => account.workspaceId === workspace.id && accountProblem(account)
            ).length;
            return (
              <div className="checklist-item" key={workspace.id}>
                <span className="agency-mark">{workspace.name[0]}</span>
                <div style={{ flex: 1 }}>
                  <strong>{workspace.name}</strong>
                  <div className="muted">
                    {problems
                      ? translate('{n} connection issue', { n: problems })
                      : translate('Healthy')}{' '}
                    ·{' '}
                    {translate('{n} for review', {
                      n: posts.filter((post) => post.status === 'Needs your review').length,
                    })}
                  </div>
                </div>
                <button className="button small" onClick={() => onOpenWorkspace(workspace.id)}>
                  {translate('Open {name}', { name: workspace.name })}
                </button>
                <button
                  className="icon-button"
                  onClick={() => onViewClient(workspace.id)}
                  aria-label={translate('View {name} as the client', { name: workspace.name })}
                  title={translate('View as client')}
                >
                  <SafeIcon name="FiEye" />
                </button>
              </div>
            );
          })}
        </div>
      </div>
      {replying &&
        (() => {
          const replyRequest = state.requests.find((request) => request.id === replying)!;
          const replyWorkspaceName = workspaces.find(
            (workspace) => workspace.id === replyRequest.workspaceId
          )?.name;
          return (
            <div
              className="drawer-backdrop"
              onMouseDown={(event) => {
                if (event.target === event.currentTarget) setReplying(null);
              }}
            >
              <section
                className="drawer"
                role="dialog"
                aria-modal="true"
                aria-labelledby="reply-title"
              >
                <div className="drawer-header">
                  <div>
                    <h2 id="reply-title">
                      {translate('Reply to {name}', { name: replyRequest.from })}
                    </h2>
                    <div className="muted">
                      {replyWorkspaceName} · {replyRequest.at}
                    </div>
                  </div>
                  <button
                    className="icon-button"
                    onClick={() => setReplying(null)}
                    aria-label={translate('Close')}
                  >
                    <SafeIcon name="FiX" />
                  </button>
                </div>
                <blockquote className="request-quote">{replyRequest.text}</blockquote>
                <label className="wizard-field">
                  {translate('Your reply')}
                  <textarea
                    className="text-field"
                    rows={5}
                    autoFocus
                    value={reply}
                    onChange={(event) => setReply(event.target.value)}
                    placeholder={translate(
                      'For example: Yes, I have added three posts for your Christmas hours. Please review them.'
                    )}
                  />
                </label>
                <p className="muted small-print">
                  {translate('{name} sees your reply in AMY and gets a notification.', {
                    name: replyRequest.from,
                  })}
                </p>
                <div className="picker-footer">
                  <button className="button" onClick={() => setReplying(null)}>
                    {translate('Cancel')}
                  </button>
                  <button
                    className="button primary"
                    disabled={reply.trim().length < 2}
                    onClick={() => {
                      commands.replyToRequest(replyRequest.id, reply.trim(), actor);
                      setReplying(null);
                      notify(translate('Reply sent to {name}.', { name: replyRequest.from }));
                    }}
                  >
                    <SafeIcon name="FiSend" />
                    {translate('Send reply')}
                  </button>
                </div>
              </section>
            </div>
          );
        })()}
    </div>
  );
}
